import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SettingDto } from '@app/shared';
import { PageHeader } from '../../components/PageHeader';
import { Button, Card, Spinner, Toast } from '../../components/ui';
import { useT } from '../../i18n';
import { api } from '../../lib/api';
import { parseSettingValue } from '../../lib/format';
import { haptic } from '../../lib/tma';

function SettingRow({ setting, onSaved }: { setting: SettingDto; onSaved: () => void }) {
  const t = useT();
  const qc = useQueryClient();
  const [draft, setDraft] = useState(String(setting.value));

  // Another row's save returns the whole list: keep an untouched field in sync.
  useEffect(() => setDraft(String(setting.value)), [setting.value]);

  const save = useMutation({
    mutationFn: (value: number) => api.adminUpdateSetting({ key: setting.key, value }),
    onSuccess: (res) => {
      qc.setQueryData(['admin', 'settings'], res);
      qc.invalidateQueries({ queryKey: ['pricing'] });
      haptic('success');
      onSaved();
    },
  });

  const parsed = parseSettingValue(draft);
  const changed = parsed !== null && parsed !== setting.value;

  return (
    <Card variant="surface" padding="md">
      <label className="block text-sm mb-2" htmlFor={`s-${setting.key}`}>
        {setting.description}
      </label>
      <div className="flex gap-2">
        <input
          id={`s-${setting.key}`}
          inputMode="numeric"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          className="flex-1 min-w-0 rounded-lg px-4 py-2.5 text-sm tabular-nums bg-surface-2 border-0 focus:outline-none focus:ring-2 focus:ring-ink/70"
        />
        <Button
          variant="ink"
          size="md"
          disabled={!changed}
          loading={save.isPending}
          onClick={() => parsed !== null && save.mutate(parsed)}
        >
          {t.common.save}
        </Button>
      </div>
      {parsed === null && <p className="text-xs text-destructive mt-1">{t.admin.settingsBad}</p>}
      {save.isError && <p className="text-xs text-destructive mt-1">{t.common.failed}</p>}
    </Card>
  );
}

export function AdminSettingsPage() {
  const t = useT();
  const [saved, setSaved] = useState(false);
  const list = useQuery({
    queryKey: ['admin', 'settings'],
    queryFn: () => api.adminListSettings(),
  });

  const flash = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  return (
    <div className="min-h-screen p-4 max-w-lg mx-auto pb-12 safe-top safe-bottom">
      <PageHeader title={t.admin.settings} to="/admin" />
      <p className="text-xs text-hint mb-3">{t.admin.settingsHint}</p>

      {list.isLoading && (
        <div className="flex justify-center py-6">
          <Spinner />
        </div>
      )}
      {list.isError && <p className="text-sm text-hint">{t.common.failed}</p>}

      <div className="space-y-2">
        {list.data?.settings.map((s) => <SettingRow key={s.key} setting={s} onSaved={flash} />)}
      </div>

      {saved && (
        <Toast tone="success" placement="fixed">
          {t.admin.settingsSaved}
        </Toast>
      )}
    </div>
  );
}
